/**
 * useServiceWorker - Centralized service worker management hook
 * Handles registration, update detection, and controlled activation
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { updateChannel, broadcastUpdateAvailable } from '@/lib/updateChannel';
import { trackEvent } from '@/lib/analytics';
import { isNativeApp } from '@/lib/platform';

// Reload loop protection
const RELOAD_COOLDOWN_MS = 5000; // 5 seconds minimum between reloads
const MAX_RELOADS_PER_SESSION = 3;
const RELOAD_COUNT_KEY = 'postpartner_reload_count';
const LAST_RELOAD_KEY = 'postpartner_sw_reload_time';

export interface ServiceWorkerState {
  /** Whether a new SW is waiting to activate */
  updateWaiting: boolean;
  /** Whether the SW is currently being registered/updated */
  isUpdating: boolean;
  /** The waiting SW registration (if any) */
  waitingWorker: ServiceWorker | null;
  /** Whether the SW is controlling the page */
  isControlled: boolean;
  /** Registration object */
  registration: ServiceWorkerRegistration | null;
  /** Trigger the waiting SW to activate and reload */
  applyUpdate: () => void;
  /** Dismiss the update (user can apply later) */
  dismissUpdate: () => void;
  /** Force check for SW updates */
  checkForUpdate: () => Promise<void>;
}

/**
 * Check if we're in a reload loop
 */
function isReloadLoopDetected(): boolean {
  try {
    const lastReload = parseInt(sessionStorage.getItem(LAST_RELOAD_KEY) || '0', 10);
    const reloadCount = parseInt(sessionStorage.getItem(RELOAD_COUNT_KEY) || '0', 10);
    const now = Date.now();

    // Too many reloads in this session
    if (reloadCount >= MAX_RELOADS_PER_SESSION) {
      console.warn('[SW] Reload loop detected: max reloads exceeded');
      return true;
    }

    // Reload too soon after last reload
    if (now - lastReload < RELOAD_COOLDOWN_MS) {
      console.warn('[SW] Reload loop detected: too soon after last reload');
      return true;
    }

    return false;
  } catch {
    return false;
  }
}

/**
 * Track reload for loop detection
 */
function trackReload(): void {
  try {
    const count = parseInt(sessionStorage.getItem(RELOAD_COUNT_KEY) || '0', 10);
    sessionStorage.setItem(RELOAD_COUNT_KEY, String(count + 1));
    sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable
  }
}

export function useServiceWorker(): ServiceWorkerState {
  const [updateWaiting, setUpdateWaiting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const [isControlled, setIsControlled] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [dismissed, setDismissed] = useState(false);

  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);

  // Apply update: tell SW to skipWaiting, then reload
  const applyUpdate = useCallback(() => {
    if (isReloadLoopDetected()) {
      console.error('[SW] Blocked reload due to loop detection');
      return;
    }

    const sw = waitingWorker || registrationRef.current?.waiting;
    if (sw) {
      trackReload();
      trackEvent('update_applied');

      // Tell the waiting SW to activate
      sw.postMessage({ type: 'SKIP_WAITING' });
    }
  }, [waitingWorker]);

  const dismissUpdate = useCallback(() => {
    setDismissed(true);
    setUpdateWaiting(false);
    trackEvent('update_dismissed');
  }, []);

  const checkForUpdate = useCallback(async () => {
    if (registrationRef.current) {
      try {
        await registrationRef.current.update();
      } catch (error) {
        console.warn('[SW] Update check failed:', error);
      }
    }
  }, []);

  // Handle waiting SW detection
  const handleWaitingSW = useCallback((sw: ServiceWorker) => {
    setWaitingWorker(sw);
    if (!dismissed) {
      setUpdateWaiting(true);
      trackEvent('update_found');
      // Notify other tabs
      broadcastUpdateAvailable(sw.scriptURL);
    }
  }, [dismissed]);

  useEffect(() => {
    // Skip SW in native Capacitor wrapper - native apps use bundled assets
    // SW caching can cause version mismatches and blank screens
    if (isNativeApp()) {
      // Unregister any existing SW to ensure clean state
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          registrations.forEach((reg) => reg.unregister());
        });
      }
      return;
    }

    if (!('serviceWorker' in navigator)) {
      return;
    }

    let mounted = true;

    const registerSW = async () => {
      try {
        setIsUpdating(true);
        const reg = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
          updateViaCache: 'none',
        });

        if (!mounted) return;

        registrationRef.current = reg;
        setRegistration(reg);
        setIsControlled(!!navigator.serviceWorker.controller);
        trackEvent('sw_registered');

        // Check if there's already a waiting worker
        if (reg.waiting) {
          handleWaitingSW(reg.waiting);
        }

        // Listen for new SW installing
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (!newWorker) return;

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New SW installed while we had an existing one = update available
              handleWaitingSW(newWorker);
            }
          });
        });

        setIsUpdating(false);
      } catch (error) {
        console.error('[SW] Registration failed:', error);
        setIsUpdating(false);
      }
    };

    // Listen for controller change (SW activated)
    const handleControllerChange = () => {
      // New SW took control - reload to get fresh assets
      if (!isReloadLoopDetected()) {
        trackReload();
        window.location.reload();
      }
    };

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    // Listen for messages from other tabs
    const unsubscribe = updateChannel.subscribe((message) => {
      if (message.type === 'UPDATE_AVAILABLE' && !dismissed) {
        setUpdateWaiting(true);
      }
    });

    registerSW();

    return () => {
      mounted = false;
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      unsubscribe();
    };
  }, [handleWaitingSW, dismissed]);

  return {
    updateWaiting: updateWaiting && !dismissed,
    isUpdating,
    waitingWorker,
    isControlled,
    registration,
    applyUpdate,
    dismissUpdate,
    checkForUpdate,
  };
}
